import { createApi } from '@reduxjs/toolkit/query/react';
import { Vehicle } from '@typeRoots/common/vehicles'; // Assuming Vehicle type exists
import { setAllVehicles, setVehicle, updateVehicle, deleteVehicle } from '../slice/vehicleSlice';
import { baseQueryWithProgress } from 'src/helpers/fetchQueryHelper';

export const vehiclesApi = createApi({
  // Unique key for the API slice in Redux state
  reducerPath: 'vehiclesApi',

  // Configure base query settings, including the base URL for all requests
  baseQuery: baseQueryWithProgress,

  // Define cache tag types for automatic cache invalidation
  tagTypes: ['Vehicles', 'Vehicle'],

  // Define API endpoints (queries and mutations)
  endpoints: (builder) => ({
    getAllVehicles: builder.query<Vehicle[], number>({
      query: (id) => `vehicles/company/${id}`, // Fetch vehicles for the current company
      async onQueryStarted(_params, { dispatch, queryFulfilled }) {
        try {
          const { data: vehicles } = await queryFulfilled; // vehicles response array
          dispatch(setAllVehicles(vehicles)); // Update all vehicles in the Redux store
        } catch (error) {
          console.log('Error fetching all vehicles', error);
        }
      },
      // Tagging vehicles to automatically refresh this cache when needed
      providesTags: ['Vehicles'],
    }),
    decodeVin: builder.query<Partial<Vehicle>, string>({
      query: (vin) => ({
        url: `/nhtsa/vin/decode?vin=${vin}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      async onQueryStarted(vin, { queryFulfilled }) {
        try {
          await queryFulfilled;
          //setCurrentVehicle(data)
        } catch (error) {
          console.log('Error decoding VIN', error);
        }
      },
    }),
    uploadVinImage: builder.mutation<Partial<Vehicle>, FormData>({
      query: (formData) => ({
        url: '/vin-extractor/vin', // Image upload endpoint
        method: 'POST',
        body: formData,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;

          // Optionally, trigger a refetch of vehicles or update Redux state if needed
        } catch (error) {
          console.log('Error uploading image', error);
        }
      },
    }),
    getVehicleById: builder.query<Vehicle, number>({
      query: (id) => `/vehicles/${id}`,
      async onQueryStarted(queryFulfilled) {
        try {
          await queryFulfilled;
          // Additional reducer functions if needed
        } catch (error) {
          console.log('Error fetching vehicle', error);
        }
      },
      // Tagging Vehicle to automatically refresh this cache when needed
      providesTags: ['Vehicle'],
    }),

    // Create vehicle endpoint
    createVehicle: builder.mutation<Vehicle, Partial<Vehicle>>({
      query: (newVehicle) => ({
        url: '/vehicles',
        method: 'POST',
        body: newVehicle,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      async onQueryStarted(vehicle, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setVehicle(data));
        } catch (e) {
          console.log('Create Vehicle error: ', e);
        }
      },
    }),

    // Update vehicle endpoint
    updateVehicle: builder.mutation<Vehicle, { id: number; updateVehicleData: Partial<Vehicle> }>({
      query: ({ id, updateVehicleData }) => ({
        url: `/vehicles/${id}`,
        method: 'PATCH',
        body: JSON.stringify(updateVehicleData),
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(updateVehicle({ ...params.updateVehicleData, id: params.id }));
        } catch (e) {
          console.log('Update Vehicle data error', e);
        }
      },
    }),

    // Delete a vehicle by ID
    deleteVehicleById: builder.mutation<void, number>({
      query: (id) => ({
        url: `/vehicles/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(deleteVehicle(id)); // Delete this vehicle from vehicles array in the store
        } catch (e) {
          console.log('Delete Vehicle error', e);
        }
      },
      // Invalidate the cache for the deleted vehicle and refetch the list
      invalidatesTags: ['Vehicle'],
    }),
  }),
});

// Export generated hooks for each endpoint to use them in components
export const {
  useGetVehicleByIdQuery,
  useGetAllVehiclesQuery,
  useDecodeVinQuery,
  useLazyDecodeVinQuery,
  useLazyGetAllVehiclesQuery,
  useLazyGetVehicleByIdQuery,
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
  useDeleteVehicleByIdMutation,
  useUploadVinImageMutation,
} = vehiclesApi;
